import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Container, HooksService, Store } from 'basic-components';
import Query from 'dashboard-services/api/Query';
import { CommonService, ReduxService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { apiConfigActions, Fetching, globalActions, loginActions, userAuthQueries } from 'primary-components';

import { creatorSeriesActions } from 'actions/series';
import { basicSettingsActions } from 'actions/settings';
import getApiConfig from 'api/ApiConfig';
import { seriesHooks } from 'hooks';
import { dataSharingQueries } from 'queries/datasharing';
import { routing } from 'routing';
import { AuthSingleton, AuthUtils, CurvesUtils, ExcelUtils, GAUtils, TimeSeriesUtils } from 'utils';

import { creatorCurvesActions } from '../../actions/curves';
import Authed from './Authed';
import Active from './Authed/Active';
import Curves from './Authed/Curves';
import DataLake from './Authed/DataLake';
import Series from './Authed/Series';
import Upload from './Authed/Upload';
import ErrorBoundary from './ErrorBoundary';
import LoginPageWrapper from './LoginPageWrapper';
import Settings from './Settings';

import { Case, Default, Else, If, Switch, Then } from 'react-if';

import './App.scss';

const emptyArray = []
const useLoadProvider = ({ tokenExist }) => {
  const dispatch = useDispatch(),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
            .withShouldDisplayError(false)
        , [getApiConfigLocal]),
        { data: providers = emptyArray } = dataSharingQueries.useGetProducers({ 
          getQueryBuilder,
          includeSelfProducer: true,
          enabled: tokenExist
        }),
        localProvider = useMemo(() => providers.find(p => p.producerId === 0), [providers])

  useEffect(() => {
    if(tokenExist) {
      dispatch(globalActions.onChange(localProvider?.name, "localSource"))
      dispatch(globalActions.onChange(providers, "sources"))
      if(!localProvider?.name) {
        console.error("Unable to locate the current provider")
        dispatch(AuthUtils.sendError({ error: "(Load providers) Unable to locate the current provider" }))
      }
    }
  }, [dispatch, localProvider, providers, tokenExist])
}

const useLoadUserSettings = ({ tokenExist, token, userName }) => {
  const dispatch = useDispatch(),
        getQueryBuilderNoError = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withShouldDisplayError(false)
        , [dispatch]),
        { mutateAsync: logOutCall } = userAuthQueries.useLogOutMutation({ getQueryBuilder: getQueryBuilderNoError }),
        getQueryBuilderSettings = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnError(e => {
              dispatch(AuthUtils.sendError({ error: "(Load settings) " + e.stack }))
              dispatch(loginActions.logOut({ logOutCall, withSideEffects: false }))
            })
            .withFetching(false)
        , [dispatch, logOutCall]),
        { data: settingsResponse } = userAuthQueries.useGetCurrentUserSettings({ 
          token,
          userName,
          entitlements: true,
          getQueryBuilder: getQueryBuilderSettings,
          enabled: tokenExist && !userName
        })

  useEffect(() => {
    if(settingsResponse) {
      if(settingsResponse.settings?.excelSeriesOptimalization) {
        dispatch(basicSettingsActions.onChange(settingsResponse.settings?.excelSeriesOptimalization === "true", "excelSeriesOptimalization"))
      }
      dispatch(basicSettingsActions.onChange(settingsResponse.settings?.definedTime || TimeService.UTC, "definedTime"))
      dispatch(basicSettingsActions.onChange(settingsResponse.settings?.definedNumberFormat || ExcelUtils.numberFormatTypesObj.GENERAL, "definedNumberFormat"))
      dispatch(basicSettingsActions.onChange(settingsResponse.settings?.userName, "userName"))
      dispatch(basicSettingsActions.onChange(settingsResponse.settings?.company, "company"))
      dispatch(basicSettingsActions.onChange(CommonService.parseJson(settingsResponse.settings?.columns, basicSettingsActions.DEFAULT_COLUMNS), "columns"))
      dispatch(basicSettingsActions.onChange(CommonService.parseJson(settingsResponse.settings?.seriesResultsColumns, ), "seriesResultsColumns"))
      dispatch(basicSettingsActions.onChange(settingsResponse.uiEntitlements, "uiEntitlements"))
    }
  }, [dispatch, settingsResponse])
}

const DEFAULT_COMPONENT = routing.LAKE.name
const App = () => {
  const dispatch = useDispatch(),
        [isDone, setIsDone] = useState(false),
        [activeComponent, setActiveComponent] = useState(DEFAULT_COMPONENT),
        [isTaskPaneVisible, setIsTaskPaneVisible] = useState(true),
        { company, userName } = useSelector(state => state.settingsState),
        clickedAddress = useRef(),
        // prevUserName = HooksService.usePrevious(userName),
        token = useSelector(state => state.primaryComponentsState.loginState.token),
        messageStore = useSelector(state => state.globalState.MessageStore),
        toastsStore = useSelector(state => state.globalState.ToastsStore),
        isFetching = useSelector(state => state.globalState.fetchingRequests.filter(f => !f.noFetching).length > 0),
        tokenExist = token != null && typeof token != "undefined" && token !== "",
        width = HooksService.useWindowSize()?.width,
        onWindowResize = useCallback(() => {
          dispatch(globalActions.onChange(width, "taskPaneWidth"))
          if(width < 612){
            dispatch(globalActions.onChange(true, "isMobile"))
          } else {
            dispatch(globalActions.onChange(false, "isMobile"))
          }
        }, [dispatch, width]),
        handleIdChange = useCallback(() => {
          const id = document.body.id;
          setActiveComponent(id || DEFAULT_COMPONENT);
        }, []),
        eventFormulaRef = useRef(),
        onDoubleClick = useCallback(async ({ address, formula }) => {
          const tsParsed = await TimeSeriesUtils.validateTable({
            cell: { address },
            formula,
            returnItem: true
          })
          const curveParsed = await CurvesUtils.validateTable({
            cell: { address },
            formula,
            returnItem: true
          })
          if(tsParsed) {
            const groupedParams = tsParsed.parsedParameters.getGroupedParams()
            dispatch(creatorSeriesActions.editFormula({ 
              address: tsParsed.address, 
              keys: groupedParams.map(gp => ReduxService.updateObject(gp, { columns: gp.columns?.split(",") })),
              params: tsParsed.parsedParameters
            }))
          } else if(curveParsed) {
            const groupedParams = curveParsed.parsedParameters.getGroupedParams()
            dispatch(creatorCurvesActions.editFormula({ 
              address: curveParsed.address, 
              keys: groupedParams,
              params: curveParsed.parsedParameters
            }))
          }
        }, [dispatch]),
        doubleClickTiemoutRef = useRef(),
        setUserDefinedFilters = useCallback(value => dispatch(creatorSeriesActions.onChange(value, "userDefinedFilters")), [dispatch]),
        handleOnSingleClicked = useCallback(e => {
          clearTimeout(doubleClickTiemoutRef.current)
          if(clickedAddress.current?.address === e.address) {
            onDoubleClick({ address: e.address, formula: clickedAddress.current.formula })
            clickedAddress.current = undefined;
          } else {
            Excel?.run(async context => {
              const cell = context.workbook.getActiveCell();
              cell.load("formulas")
              await context.sync()
              const formula = cell.formulas?.[0]?.[0]
              clickedAddress.current = {
                address: e.address,
                formula
              }
              doubleClickTiemoutRef.current = setTimeout(() => {
                clickedAddress.current = undefined;
              }, 500) // default double click max for Windows
            }).catch(e => {
              console.error(e)
              dispatch(AuthUtils.sendError({ error: "(OnDoubleClick) " + e.stack }))
            })
          }
        }, [dispatch, onDoubleClick]),
        onTakeMeBack = useCallback(() => {
          Office.addin.hide()
          location.reload()
          setTimeout(() => {
            Office.addin.showAsTaskpane()
          }, 500)
        }, []),
        observer = useRef(),
        initTimeoutRef = useRef(),
        getUserFilters = seriesHooks.useUserFilters()

  useLayoutEffect(() => {
    observer.current = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'id') {
          handleIdChange();
        }
      })
    });
    observer.current.observe(document.body, { attributes: true })
  }, [handleIdChange])

  useLoadUserSettings({ tokenExist, token, userName })

  useLoadProvider({ tokenExist })

  useEffect(() => {
    handleIdChange()
  }, [handleIdChange])

  useLayoutEffect(() => {
    dispatch(loginActions.logoutAllTabs(false))
    dispatch(apiConfigActions.changeApiConfig(getApiConfig))
    AuthSingleton.getInstance().refreshNGToken.finally(() => {
      setIsDone(true)
    })
    .catch(e => {
      dispatch(AuthUtils.sendError({ error: "(RefreshNGToken)" + e.stack }))
    })
  }, [dispatch])

  useEffect(() => {
    if(!messageStore) {
      dispatch(globalActions.onChange(new Store(), "MessageStore"))
    }
    if(!toastsStore) {
      dispatch(globalActions.onChange(new Store(), "ToastsStore"))
    }
  }, [dispatch, messageStore, toastsStore])

  useEffect(() => {
    onWindowResize()
  }, [width, onWindowResize])

  useEffect(() => {
    Office?.onReady(() => {
      Office?.addin?.onVisibilityModeChanged(function(args) {
        if (args.visibilityMode === "Taskpane") {
          setIsTaskPaneVisible(true)
        } else {
          setIsTaskPaneVisible(false)
        }
      })
    })
  }, [])

  useEffect(() => {
    if(userName) {
      GAUtils.init({ userName, company })
    }
  }, [company, dispatch, userName])

  // useEffect(() => {
  //   if(userName && prevUserName !== userName) {
  //     setTimeout(() => AuthUtils.disableLogInButton(), 10)
  //   } else if(!userName && prevUserName !== userName) {
  //     AuthUtils.enableLogInButton()
  //   }
  // }, [prevUserName, userName])

  useEffect(() => {
    const initOnClick = () => {
      Excel?.run(async context => {
        const worksheets = context.workbook.worksheets
        eventFormulaRef.current = worksheets.onSingleClicked?.add(handleOnSingleClicked)
        context.sync()
      }).catch(() => {
        initTimeoutRef.current = window.setTimeout(() => initOnClick(), 1000)
      })
    }
    initOnClick()
  }, [handleOnSingleClicked])

  useEffect(() => () => {
    observer.current.disconnect()
    Excel.run(eventFormulaRef.current?.context, async context => {
      eventFormulaRef.current?.remove()
      await context.sync();
    });
    window.clearTimeout(initTimeoutRef.current)
  }, [])

  useEffect(() => {
    tokenExist && getUserFilters().then(setUserDefinedFilters)
  }, [getUserFilters, setUserDefinedFilters, tokenExist])

  return (
    <ErrorBoundary 
        onTakeMeBack={onTakeMeBack}
        shouldDisplayError={width === 0 || !isTaskPaneVisible}
    >
      <div className="ng-office-app">
        {toastsStore && (
          <Container
              globalTimeout={30000}
              position="top-right"
              store={toastsStore}
              variant="toasts"
          />
        )}
        {messageStore && (
          <Container
              globalTimeout={5000}
              position="top-center"
              store={messageStore}
              variant="message"
          />
        )}
        <If condition={!isDone}>
          <Then>
            {() => <Fetching isFetching/>}  
          </Then>
          <Else>
            {() => (
              <Fragment>
                <Fetching
                    isFetching={isFetching}
                />
                <If condition={activeComponent === routing.SETTINGS.name}>
                  <Then>
                    {() => (
                      <Settings isLoggedIn={tokenExist}/>
                    )}
                  </Then>
                  <Else>
                    {() => (
                      <Fragment>
                        <If condition={tokenExist}>
                          <Then>
                            {() => (
                              <Authed>
                                <Switch>
                                  <Case condition={activeComponent === routing.ACTIVE.name}>
                                    {() => (
                                      <Active/>
                                    )}
                                  </Case>
                                  <Case condition={activeComponent === routing.LAKE.name}>
                                    {() => (
                                      <DataLake/>
                                    )}
                                  </Case>
                                  <Case condition={activeComponent === routing.TS.name}>
                                    {() => (
                                      <Series/>
                                    )}
                                  </Case>
                                  <Case condition={activeComponent === routing.CURVES.name}>
                                    {() => (
                                      <Curves/>
                                    )}
                                  </Case>
                                  <Case condition={activeComponent === routing.UPLOAD.name}>
                                    {() => (
                                      <Upload/>
                                    )}
                                  </Case>
                                  <Default>
                                    {() => <Series/>}
                                  </Default>
                                </Switch>
                              </Authed>
                            )}
                          </Then>
                          <Else>
                            {() => (
                              <LoginPageWrapper
                                  activeComponent={activeComponent}
                              />
                            )}
                          </Else>
                        </If>
                      </Fragment>
                    )}
                  </Else>
                </If>
              </Fragment>
            )}
          </Else>
        </If>
      </div>
    </ErrorBoundary>
  )
}

export default App;
